// The SPA: the catalog's three routes over the two Worker API routes, and
// the founder-only press room under /cms over /api/cms, under one slim
// catalog header. Routing is react-router in its data-less mode. react-router
// is the estate's router (docs/plan/yominoki-port.md:19); apps/reader does
// not reopen it.
import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Link, Route, Routes } from "react-router";
import { ModeToggle } from "@bindery/ui/components/theme/mode-toggle";
import { CmsShell } from "./cms/shell";
import { LandingPage } from "./pages/landing";
import { RecommendPage } from "./pages/recommend";
import { TextDetailPage } from "./pages/text-detail";

function Header() {
  return (
    <header className="bg-card border-b">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <Link to="/" className="font-serif text-xl font-bold tracking-tight">
          Corpus
        </Link>
        <div className="flex items-center gap-4">
          <nav className="flex gap-4">
            <Link
              to="/recommend"
              className="text-muted-foreground hover:text-foreground text-sm hover:underline"
            >
              Recommendations
            </Link>
            <Link
              to="/cms"
              className="text-muted-foreground hover:text-foreground text-sm hover:underline"
            >
              Press room
            </Link>
          </nav>
          <ModeToggle />
        </div>
      </div>
    </header>
  );
}

// The press room carries the markdown tokenizer and renderer, which the
// catalog pages never need, so its screens load on first visit.
const LibraryPage = lazy(() =>
  import("./cms/pages/library").then((m) => ({ default: m.LibraryPage })),
);
const ImportPage = lazy(() =>
  import("./cms/pages/import").then((m) => ({ default: m.ImportPage })),
);
const UploadPage = lazy(() =>
  import("./cms/pages/upload").then((m) => ({ default: m.UploadPage })),
);
const DocumentPage = lazy(() =>
  import("./cms/pages/document").then((m) => ({ default: m.DocumentPage })),
);

const cms = (page: ReactNode) => (
  <CmsShell>
    <Suspense fallback={null}>{page}</Suspense>
  </CmsShell>
);

export function App() {
  return (
    <BrowserRouter>
      <div className="bg-background text-foreground min-h-screen">
        <Header />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/recommend" element={<RecommendPage />} />
          <Route path="/text/:id" element={<TextDetailPage />} />
          <Route path="/cms" element={cms(<LibraryPage />)} />
          <Route path="/cms/import" element={cms(<ImportPage />)} />
          <Route path="/cms/upload" element={cms(<UploadPage />)} />
          <Route path="/cms/documents/:id" element={cms(<DocumentPage />)} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
